"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, Clock, AlertTriangle, Star, BookOpen, Zap, Shield, X, Play, Target } from "lucide-react";
import { HOTMART_CONFIG, PRICING, UPSELL_VIDEOS } from "@/config/constants";
import { Footer } from "@/components/Footer";
import { VturbPlayer } from "@/components/VturbPlayer";

export const UpsellModuloLda = () => {
  const pricing = PRICING.upsellModuloLda;
  const checkoutUrl = HOTMART_CONFIG.upsellModuloLda;

  const handleAccept = () => {
    // Track conversion
    if ((window as any).fbq) {
      (window as any).fbq("track", "InitiateCheckout", {
        content_name: "Upsell Módulo LDA",
        currency: "BRL",
        value: 297,
      });
    }
    window.location.href = checkoutUrl;
  };

  const handleDecline = () => {
    // Redirecionar para página de obrigado ou próximo passo
    window.location.href = "/obrigado";
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header com urgência */}
      <div className="bg-red-600 py-4 md:py-5 px-4 text-center">
        <div className="flex items-center justify-center gap-2 text-white font-bold text-base md:text-lg">
          <Clock className="h-5 w-5 md:h-6 md:w-6 animate-pulse" />
          <span>ATENÇÃO: NÃO FECHE ESTA PÁGINA! ESSA OFERTA NUNCA MAIS IRÁ SE REPETIR!</span>
        </div>
      </div>

      <section className="relative py-12 md:py-20 overflow-hidden">
        {/* Background effects */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-primary">
          <div className="absolute top-20 left-20 w-96 h-96 bg-primary/20 rounded-full blur-[100px]" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-accent/15 rounded-full blur-[100px]" />
        </div>

        <div className="container px-4 relative z-10">
          <div className="max-w-4xl mx-auto">

            {/* Headline */}
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-center leading-tight mb-6">
              <span className="bg-gradient-accent bg-clip-text text-transparent">
                ESTA É A SUA ÚNICA CHANCE DE ACELERAR SEU PROCESSO
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-muted-foreground text-center max-w-3xl mx-auto mb-10 leading-relaxed">
              com acesso completo liberado agora, por um <strong className="text-foreground">preço especial que NUNCA MAIS VAI EXISTIR!</strong>
            </p>

            {/* Vídeo de apresentação */}
            <div className="mb-6 flex justify-center">
              <div className="relative w-full max-w-sm rounded-2xl sm:rounded-3xl p-1 sm:p-2 bg-gradient-to-br from-primary/30 via-card/20 to-accent/30">
                <VturbPlayer
                  videoId={UPSELL_VIDEOS.upsellModuloLda.id}
                  scriptUrl={UPSELL_VIDEOS.upsellModuloLda.url}
                  className="relative aspect-[9/16] w-full rounded-xl sm:rounded-2xl overflow-hidden"
                />
              </div>
            </div>

             {/* Botão CTA abaixo do vídeo */}
            <div className="mb-10 flex justify-center">
              <Button
                onClick={handleAccept}
                className="w-full max-w-sm px-8 md:px-10 py-8 md:py-8 text-base md:text-xl font-bold bg-gradient-to-r from-green-400 to-emerald-500 hover:from-green-300 hover:to-emerald-400 shadow-lg shadow-green-500/50 hover:shadow-green-400/70 transition-all hover:-translate-y-1"
              >
                🔓 GARANTIR AGORA
              </Button>
            </div>

            {/* Card principal */}
            <Card className="relative border-2 border-primary/60 shadow-[0_0_60px_rgba(100,192,242,0.3)] overflow-hidden mb-10">
              {/* Glow interno */}
              <div
                className="pointer-events-none absolute inset-0 opacity-60"
                style={{
                  background: "radial-gradient(800px 300px at 50% 0%, hsl(var(--primary)/0.15), transparent 70%)",
                }}
              />

              <CardContent className="relative p-6 md:p-10">
                {/* Título do produto */}
                <div className="text-center mb-8">
                  <h2 className="text-2xl md:text-3xl font-bold mb-2">
                    📚 Módulo Completo do Método LDA
                  </h2>
                  <p className="text-muted-foreground">
                    Acesso total a todas as aulas, técnicas e exercícios para uma transformação definitiva
                  </p>
                </div>

                {/* O que está incluso */}
                <div className="grid md:grid-cols-2 gap-4 mb-8">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">+20 aulas em vídeo</p>
                      <p className="text-sm text-muted-foreground">Conteúdo completo e aprofundado</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">Técnicas de neurociência</p>
                      <p className="text-sm text-muted-foreground">Reprograme seu cérebro para eliminar o vício</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">Exercícios práticos diários</p>
                      <p className="text-sm text-muted-foreground">Atividades para consolidar a mudança</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">Protocolo anti-recaída</p>
                      <p className="text-sm text-muted-foreground">Sistema comprovado de proteção</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">Comunidade exclusiva</p>
                      <p className="text-sm text-muted-foreground">Grupo de apoio com outros alunos</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">Acesso vitalício</p>
                      <p className="text-sm text-muted-foreground">Estude no seu ritmo, para sempre</p>
                    </div>
                  </div>
                </div>

                {/* Comparação */}
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  {/* Sem upgrade */}
                  <div className="bg-red-950/30 border border-red-500/30 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <X className="h-5 w-5 text-red-400" />
                      <h3 className="font-bold text-red-400">Sem o módulo completo</h3>
                    </div>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>❌ Acesso apenas ao conteúdo básico</li>
                      <li>❌ Técnicas superficiais</li>
                      <li>❌ Sem protocolo anti-recaída</li>
                      <li>❌ Risco maior de voltar a apostar</li>
                      <li>❌ Transformação incompleta</li>
                    </ul>
                  </div>

                  {/* Com upgrade */}
                  <div className="bg-emerald-950/30 border border-emerald-500/30 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                      <h3 className="font-bold text-emerald-400">Com o módulo completo</h3>
                    </div>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>✅ Todas as aulas e técnicas avançadas</li>
                      <li>✅ Reprogramação mental profunda</li>
                      <li>✅ Protocolo completo anti-recaída</li>
                      <li>✅ Proteção definitiva contra o vício</li>
                      <li>✅ Transformação real e duradoura</li>
                    </ul>
                  </div>
                </div>

                {/* Módulos inclusos */}
                <div className="bg-card/50 border border-border rounded-xl p-6 mb-8">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-primary" />
                    O que você vai aprender:
                  </h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="flex items-center gap-2">
                      <Play className="h-4 w-4 text-primary" />
                      <span className="text-sm">Módulo 1: Entendendo o Vício</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Play className="h-4 w-4 text-primary" />
                      <span className="text-sm">Módulo 2: Gatilhos e Armadilhas</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Play className="h-4 w-4 text-primary" />
                      <span className="text-sm">Módulo 3: Reprogramação Mental</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Play className="h-4 w-4 text-primary" />
                      <span className="text-sm">Módulo 4: Técnicas de Neurociência</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Play className="h-4 w-4 text-primary" />
                      <span className="text-sm">Módulo 5: Protocolo Anti-Recaída</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Play className="h-4 w-4 text-primary" />
                      <span className="text-sm">Módulo 6: Vida Sem Apostas</span>
                    </div>
                  </div>
                </div>

                {/* Preço */}
                <div className="text-center mb-8">
                  <div className="flex flex-col items-center gap-2 mb-4">
                    <span className="text-lg text-muted-foreground">
                      De <span className="line-through text-red-400">{pricing.priceOriginal}</span>
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-yellow-500/20 px-4 py-1 text-sm font-bold uppercase tracking-wide text-yellow-400">
                      Upgrade exclusivo desta página
                    </span>
                  </div>
                  <div className="text-lg text-muted-foreground mb-1">Por apenas</div>
                  <div className="text-5xl md:text-7xl font-black text-white mb-2">
                    {pricing.price}
                  </div>
                  <div className="text-muted-foreground">
                    ou {pricing.priceInstallment} em {pricing.installments} no cartão
                  </div>
                </div>

                {/* CTA */}
                <div className="flex flex-col items-center gap-4">
                  <Button
                    onClick={handleAccept}
                    className="w-full max-w-md px-6 md:px-8 py-6 text-sm md:text-xl font-bold bg-gradient-to-r from-green-400 to-emerald-500 hover:from-green-300 hover:to-emerald-400 shadow-lg shadow-green-500/50 hover:shadow-green-400/70 transition-all hover:-translate-y-1"
                  >
                    🔓 SIM! QUERO O ACESSO COMPLETO
                  </Button>
                  
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Shield className="h-4 w-4 text-emerald-400" />
                    <span>Pagamento 100% seguro • Garantia de 7 dias</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Seção "E se eu não pegar isso agora?" */}
            <div className="bg-gradient-to-br from-red-950/40 to-orange-950/30 border border-red-500/30 rounded-2xl p-6 md:p-8 mb-10">
              <div className="flex items-start gap-4">
                <AlertTriangle className="h-8 w-8 text-yellow-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl md:text-2xl font-bold mb-4 text-yellow-400">
                    E se eu não aproveitar esse upgrade agora?
                  </h3>
                  <div className="space-y-3 text-muted-foreground">
                    <p>
                      Você pode seguir apenas com o conteúdo básico. Muita gente faz isso. 
                      Mas a realidade é que <strong className="text-foreground">sem as técnicas completas, 
                      a transformação fica incompleta</strong>.
                    </p>
                    <p>
                      É como ter a chave de uma porta, mas não saber exatamente como usá-la. 
                      O módulo completo é o <strong className="text-foreground">manual passo a passo</strong> que 
                      vai te mostrar exatamente o que fazer em cada situação.
                    </p>
                    <p>
                      Esta oferta <strong className="text-foreground">só existe nesta página, neste momento</strong>. 
                      Se você sair, vai precisar pagar o valor cheio depois — se ainda estiver disponível.
                    </p>
                    <p className="text-foreground font-semibold">
                      Você veio até aqui porque quer mudar de verdade. Dê a si mesmo a melhor chance de conseguir.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Botão de recusar (discreto) */}
            <div className="text-center">
              <button
                onClick={handleDecline}
                className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors"
              >
                Não, obrigado. Vou seguir apenas com o conteúdo básico.
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UpsellModuloLda;
